import React from 'react'
import { Column, useGlobalFilter, usePagination, useRowState, useSortBy, useTable } from 'react-table'
import { Link, RouteComponentProps } from 'react-router-dom'
import classNames from 'classnames'
import { withRouter } from 'react-router-dom'
import ReactTablePagination from 'src/components/ui/ReactTablePagination'
import { MoonLoader } from 'react-spinners'
import { IActiveTripResponseV2, ITripFilters, IServiceObject, IOrgCreateObj } from 'src/types'
import moment from 'moment'
import { EyeClose, EyeOpen } from 'src/components/icons/main-menu'
import Audit from 'src/components/trips/trip-detail/Audit'
import { DropdownCell } from 'src/components/trip-feedbacks/DropdownCell'
import FloatingForm from 'src/components/ui/form/FloatingForm'

const PAGE_SIZE = 25

export interface ITableFeedbackProps extends RouteComponentProps<any> {
  ownProps: RouteComponentProps<any>
  orgId: string
  riderId: string
  fetchFeedbacks: (params: ITripFilters) => Promise<any>
  updateTripFilters: (params: ITripFilters) => Promise<any>
  updateFeedbackStatus: (tripId: string, feedbackStatus: string, prevFeedbackStatus: string) => Promise<any>
  filters: ITripFilters
  searchFilter: string
  services: IServiceObject[]
  organizations: IOrgCreateObj | null
}
interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: any[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
  updateFilters: (params: ITripFilters) => Promise<any>
  filters: ITripFilters
  history: any
  inputSearchFilter: string
  organizations: IOrgCreateObj | null
}
function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  loading,
  pageCount: controlledPageCount,
  filters,
  inputSearchFilter,
  organizations
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE,
        sortBy: [
          {
            id: 'created',
            desc: true
          }
        ]
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowState
  )

  const { pageIndex, sortBy, pageSize } = state

  React.useEffect(() => {
    setGlobalFilter(inputSearchFilter)
  }, [inputSearchFilter])

  React.useEffect(() => {
    fetchData({ pageIndex, pageSize })
  }, [pageIndex, pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    updateFilters({ sortBy: sortParams })
  }, [sortBy])

  React.useEffect(() => {
    gotoPage(0)
  }, [filters])

  const showAuditTrail = (row: any) => {
    setSelectedRow(row.id)
    openAudit(true)
  }

  const closeAuditTrail = () => {
    openAudit(false)
  }

  const [showAudit, setShowAudit] = React.useState(false)
  const [selectedRow, setSelectedRow] = React.useState('')
  const [auditOpened, openAudit] = React.useState(false)

  console.log(data)

  return (
    <React.Fragment>
      <table
        {...getTableProps()}
        style={{
          fontFamily: 'Open Sans',
          borderCollapse: 'collapse',
          width: '100%'
        }}
      >
        <thead className='header'>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
              <th>{'Audit Trail'}</th>
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()} key={`rowFeedbackTable-${row.index}`}>
                {row.cells.map(cell => {
                  return (
                    <td
                      className='data-cell audit-col'
                      {...cell.getCellProps()}
                      key={`columnFeedbackTable-${cell.column.index}`}
                    >
                      {cell.render('Cell')}
                    </td>
                  )
                })}
                <td key={`showAudit-${row.id}`}>
                  {selectedRow === row.id && auditOpened ? (
                    <div onClick={() => closeAuditTrail()}>
                      <EyeOpen style={{ margin: 'auto' }} className='mr1 block' />
                      <div className='driver-feedback-audit'>
                        <Audit
                          auditTrail={data[row.index] ? data[row.index].tripEditsHistory : []}
                          timeFormat={organizations ? organizations.timeFormat : 'mil'}
                        />
                      </div>
                    </div>
                  ) : (
                    <div onClick={() => showAuditTrail(row)}>
                      <EyeClose style={{ cursor: 'pointer', margin: 'auto' }} className='mr1 block' />
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {loading ? (
        <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
        </div>
      ) : null}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const CellBasicInfo = (props: { value: string; tripId: string }) => (
  <div className='flex items-center'>
    <Link to={`/trips/${props.tripId}`}>
      <div className='ml1 TeamMemberName font-dark-grey'>{props.value}</div>
    </Link>
  </div>
)

function RiderFeedback(props: ITableFeedbackProps) {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const columns = React.useMemo(
    () => [
      {
        id: 'created',
        Header: 'Created At',
        accessor: 'lastStatusAt',
        className: 'flex items-center',
        Cell: (data: any) => {
          const { id, lastStatusAt } = data.row.original
          return <CellBasicInfo value={moment(lastStatusAt).format('M/D/YYYY hh:mmA')} tripId={id} />
        }
      },
      {
        id: 'customer',
        Header: 'Rider Name',
        className: 'flex items-center',
        accessor: (trip: IActiveTripResponseV2) => {
          const { rider } = trip
          return <span>{`${rider.firstName} ${rider.lastName}`}</span>
        }
      },
      {
        id: 'riderFeedback', // id name is like this because of the regex for column filtering check filterCols function
        Header: 'Rider Feedback',
        disableSortBy: true,
        accessor: ({ riderPositiveFeedback, riderNegativeFeedback }: any) => {
          return (
            <>
              {riderPositiveFeedback || riderNegativeFeedback ? (
                <>
                  <span>{riderPositiveFeedback ? riderPositiveFeedback : ''}</span>
                  <span>{riderNegativeFeedback ? riderNegativeFeedback : ''}</span>
                </>
              ) : (
                '-'
              )}
            </>
          )
        },
        className: 'flex items-center'
      },
      {
        id: 'riderFeedbackType',
        Header: 'Type',
        disableSortBy: true,
        accessor: ({ isRiderPositiveFeedback }: any) => {
          return (
            <>
              {isRiderPositiveFeedback !== null ? (
                <span className={isRiderPositiveFeedback ? 'positive' : 'negative'}>
                  {isRiderPositiveFeedback ? 'Positive' : 'Negative'}
                </span>
              ) : (
                '-'
              )}
            </>
          )
        },
        className: 'flex items-center'
      },
      {
        id: 'status',
        Header: 'Status',
        disableSortBy: true,
        accessor: 'feedbackStatus',
        className: 'flex items-center',
        Cell: (data: any) => {
          const { feedbackStatus, id } = data.row.original
          return (
            <DropdownCell feedbackStatus={feedbackStatus} id={id} updateFeedbackStatus={props.updateFeedbackStatus} />
          )
        }
      }
    ],
    [props.filters]
  )

  React.useEffect(() => {
    if (props.filters && Object.keys(props.filters).length !== 0) {
      //Do not load date until filters are set
      fetchData({ pageIndex: 0, pageSize })
    }
  }, [props.orgId, props.filters])

  const fetchData = React.useCallback(async ({ pageSize, pageIndex }) => {
    setLoading(true)
    if (props.filters.orgId || (props.filters.productIds && props.filters.productIds.length)) {
      const data = await props.fetchFeedbacks({
        ...props.filters,
        riderId: [props.riderId],
        skip: pageIndex * pageSize,
        take: pageSize
      })
      if (data && data.items) {
        setData(data.items)
        setLoading(false)
        setPageCount(Math.ceil(data.total / pageSize))
        setPageSize(pageSize)
      }
    }
  }, [props.orgId, props.filters])

  return (
    <FloatingForm fullHeight={true}>
      <div className='react-table w100'>
        <div className='font-grey2 bold h5 mb1'>Rider Feedback</div>
        <div className='mt1 w100 feedback-table'>
          <Table
            columns={columns}
            data={data}
            fetchData={fetchData}
            filters={props.filters}
            updateFilters={props.updateTripFilters}
            loading={loading}
            pageCount={pageCount}
            history={props.history}
            inputSearchFilter={props.searchFilter}
            organizations={props.organizations}
          />
        </div>
      </div>
    </FloatingForm>
  )
}

export default withRouter(RiderFeedback)



// WEBPACK FOOTER //
// ./src/components/riders/riderdetails/RiderFeedback.tsx